import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, TitlePlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { AccountSignalTypeIcon } from "../AccountSignalTypeIcon/AccountSignalTypeIcon";

import { StyledAccountSignalTypeCard } from "./styles";

type AccountSignalTypeCardProps = {
    title: string;
    icon: ReactNode;
    dataAutomation: string;
    count?: number;
    isCountLoading?: boolean;
    isComingSoon?: boolean;
    isClickable?: boolean;
    onClick?: VoidFunction;
};

export const AccountSignalTypeCard = ({
    title,
    icon,
    dataAutomation,
    count,
    isCountLoading = false,
    isComingSoon = false,
    isClickable = true,
    onClick,
}: AccountSignalTypeCardProps) => {
    const { translate } = useTranslate();

    const configuredLabel =
        isCountLoading || count === undefined
            ? null
            : count === 1
              ? translate("si.account-signals.management.signals.configured.singular")
              : translate("si.account-signals.management.signals.configured.plural", { count });

    return (
        <StyledAccountSignalTypeCard
            type="button"
            $isClickable={Boolean(isClickable && !isComingSoon)}
            onClick={isComingSoon ? undefined : onClick}
            data-automation={dataAutomation}
            disabled={isComingSoon || !isClickable}
        >
            <Flex
                width="100%"
                justifyContent="space-between"
                alignItems="flex-start"
                gap="8px"
            >
                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    <Text
                        fontVariant="subsectionTitle"
                        color={colorsPalettes.carbon[400]}
                    >
                        {title}
                    </Text>
                    {isComingSoon ? <Badge type="coming soon" /> : null}
                </Flex>
                <AccountSignalTypeIcon>{icon}</AccountSignalTypeIcon>
            </Flex>
            {isCountLoading ? (
                <TitlePlaceholderLoader
                    id={`${dataAutomation}-title-loader`}
                    className="Loader--title"
                />
            ) : count !== undefined ? (
                <Text
                    fontVariant="h2"
                    color={colorsPalettes.carbon[500]}
                >
                    {count}
                </Text>
            ) : null}
            {configuredLabel ? (
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[300]}
                >
                    {configuredLabel}
                </Text>
            ) : null}
        </StyledAccountSignalTypeCard>
    );
};
